import React, { useState, useEffect, useCallback } from 'react';
import { generateInvoicePL, lookupPrice, getSuppliers } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { FileSpreadsheet, Plus, Trash2, Loader2, Download, Check, AlertTriangle } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const EMPTY_ITEM = { item_no: '', description: '', price: 0, qty: 0, unit: 'PCS', ctns: 0, cbm: 0, gw: 0, nw: 0 };

export function InvoicePLGenerator({ mbl, shipment, containers }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const isAluminum = shipment?.cargo_type?.toUpperCase().includes('ALUMIN');

  const [config, setConfig] = useState({
    supplier_name: '',
    supplier_address: '',
    invoice_no: '',
    invoice_date: new Date().toISOString().slice(0, 10),
    shipment_date: '',
    signatory_name: '',
    shipping_mark_lines: '',
    price_mode: 'standard',
  });

  const [items, setItems] = useState([{ ...EMPTY_ITEM, item_no: '1' }]);
  const [priceSuggestions, setPriceSuggestions] = useState({});
  const [priceMatches, setPriceMatches] = useState({});

  useEffect(() => {
    if (!shipment || !open) return;
    const fillSupplier = async () => {
      try {
        const res = await getSuppliers();
        const sup = res.data.find(s => s.id === shipment.supplier_id);
        if (sup) {
          setConfig(prev => ({
            ...prev,
            supplier_name: prev.supplier_name || sup.name || shipment.supplier_name || '',
            supplier_address: prev.supplier_address || sup.address || '',
          }));
        } else {
          setConfig(prev => ({
            ...prev,
            supplier_name: prev.supplier_name || shipment.supplier_name || '',
          }));
        }
      } catch { /* ignore */ }
    };
    fillSupplier();
    setConfig(prev => ({
      ...prev,
      invoice_date: shipment.etd_final_destination || shipment.fecha_embarque || prev.invoice_date,
      shipment_date: shipment.etd_final_destination || shipment.fecha_embarque || prev.shipment_date,
    }));
  }, [shipment, open]);

  const lookupPriceForItem = useCallback(async (index, description) => {
    if (!description || description.length < 3) {
      setPriceSuggestions(prev => { const n = { ...prev }; delete n[index]; return n; });
      setPriceMatches(prev => { const n = { ...prev }; delete n[index]; return n; });
      return;
    }
    try {
      const res = await lookupPrice(description, config.supplier_name);
      const matches = res.data?.matches || [];
      if (matches.length > 0) {
        setPriceSuggestions(prev => ({ ...prev, [index]: matches[0] }));
      } else {
        setPriceSuggestions(prev => { const n = { ...prev }; delete n[index]; return n; });
      }
    } catch { /* ignore */ }
  }, [config.supplier_name]);

  useEffect(() => {
    const newMatches = {};
    items.forEach((item, i) => {
      const sug = priceSuggestions[i];
      if (sug && item.price > 0) {
        const diff = Math.abs(item.price - sug.unit_price);
        const pct = sug.unit_price > 0 ? (diff / sug.unit_price) * 100 : 0;
        newMatches[i] = pct < 1 ? 'match' : 'mismatch';
      }
    });
    setPriceMatches(newMatches);
  }, [items, priceSuggestions]);

  const addItem = () => {
    setItems(prev => [...prev, { ...EMPTY_ITEM, item_no: String(prev.length + 1) }]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
    setPriceSuggestions(prev => { const n = { ...prev }; delete n[index]; return n; });
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
    if (field === 'description') {
      const timer = setTimeout(() => lookupPriceForItem(index, value), 500);
      return () => clearTimeout(timer);
    }
  };

  const applySuggestion = (index) => {
    const sug = priceSuggestions[index];
    if (!sug) return;
    setItems(prev => prev.map((item, i) => i === index ? { ...item, price: sug.unit_price, unit: sug.unit || item.unit } : item));
  };

  const handleGenerate = async () => {
    if (!items.some(it => it.description)) {
      toast.error(t('common.required'));
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        ...config,
        shipping_mark_lines: config.shipping_mark_lines
          ? config.shipping_mark_lines.split('\n').filter(l => l.trim())
          : [],
        items,
      };
      const res = await generateInvoicePL(mbl, payload);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `INVOICE_PL_${mbl.replace(/\//g, '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('documents.generated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('documents.errorGenerating'));
    } finally {
      setGenerating(false);
    }
  };

  const isCtnMode = config.price_mode === 'ctn_multiply';
  const totalAmount = items.reduce((sum, it) => {
    if (isCtnMode && it.ctns > 0) return sum + it.price * it.qty * it.ctns;
    return sum + it.price * it.qty;
  }, 0);
  const totalCtns = items.reduce((sum, it) => sum + (it.ctns || 0), 0);
  const totalCbm = items.reduce((sum, it) => sum + (it.cbm || 0), 0);
  const totalGw = items.reduce((sum, it) => sum + (it.gw || 0), 0);
  const totalNw = items.reduce((sum, it) => sum + (it.nw || 0), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="generate-invoice-pl-btn"
          disabled={isAluminum}
          title={isAluminum ? t('documents.aluminumManualOnly') : ''}
        >
          <FileSpreadsheet size={14} className="mr-1.5" />
          {isAluminum ? t('documents.manualInvoiceOnly') : t('documents.generateInvoicePL')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('documents.generateInvoicePL')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Supplier Config */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('documents.supplierName')}</Label>
              <Input
                value={config.supplier_name}
                onChange={e => setConfig(prev => ({ ...prev, supplier_name: e.target.value }))}
                placeholder="COMPANY NAME CO., LTD"
                className="h-8 text-sm"
                data-testid="inv-supplier-name"
              />
            </div>
            <div>
              <Label className="text-xs">{t('documents.supplierAddress')}</Label>
              <Input
                value={config.supplier_address}
                onChange={e => setConfig(prev => ({ ...prev, supplier_address: e.target.value }))}
                placeholder="Address, City, Country"
                className="h-8 text-sm"
                data-testid="inv-supplier-address"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">{t('documents.invoiceNo')}</Label>
              <Input
                value={config.invoice_no}
                onChange={e => setConfig(prev => ({ ...prev, invoice_no: e.target.value }))}
                placeholder="INV-2026-001"
                className="h-8 text-sm"
                data-testid="inv-number"
              />
            </div>
            <div>
              <Label className="text-xs">{t('documents.invoiceDate')}</Label>
              <Input type="date" value={config.invoice_date}
                onChange={e => setConfig(prev => ({ ...prev, invoice_date: e.target.value }))}
                className="h-8 text-sm" data-testid="inv-date"
              />
            </div>
            <div>
              <Label className="text-xs">{t('documents.shipmentDate')}</Label>
              <Input type="date" value={config.shipment_date}
                onChange={e => setConfig(prev => ({ ...prev, shipment_date: e.target.value }))}
                className="h-8 text-sm" data-testid="inv-ship-date"
              />
            </div>
            <div>
              <Label className="text-xs">{t('documents.signatory')}</Label>
              <Input value={config.signatory_name}
                onChange={e => setConfig(prev => ({ ...prev, signatory_name: e.target.value }))}
                className="h-8 text-sm" data-testid="inv-signatory"
              />
            </div>
          </div>

          {/* Price Mode Toggle + Shipping Marks */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-2.5 rounded bg-secondary/30 border border-border/50">
              <Switch
                checked={isCtnMode}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, price_mode: checked ? 'ctn_multiply' : 'standard' }))}
                data-testid="inv-price-mode-toggle"
              />
              <div>
                <Label className="text-xs font-medium">
                  {isCtnMode ? 'Precio: QTY x CTN x PRICE' : 'Precio: QTY x PRICE'}
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  {isCtnMode ? 'Para maquinas (1 producto = multiples CTNs)' : 'Calculo estandar'}
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Shipping Marks</Label>
              <textarea
                value={config.shipping_mark_lines}
                onChange={e => setConfig(prev => ({ ...prev, shipping_mark_lines: e.target.value }))}
                placeholder="Una linea por marca..."
                className="w-full h-16 text-xs p-2 rounded border border-border bg-background resize-none"
                data-testid="inv-shipping-marks"
              />
            </div>
          </div>

          {/* Shipment Info Summary */}
          <div className="grid grid-cols-4 gap-3 bg-secondary/30 rounded p-2.5 text-xs">
            <div>
              <span className="text-muted-foreground">B/L:</span>
              <span className="ml-1 font-medium font-mono">{mbl}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('documents.importer')}:</span>
              <span className="ml-1 font-medium">{shipment?.importer_name || '-'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('documents.container')}:</span>
              <span className="ml-1 font-medium font-mono">{containers?.[0]?.container_number || '-'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('documents.vessel')}:</span>
              <span className="ml-1 font-medium">{shipment?.vessel_name || shipment?.buque || '-'}</span>
            </div>
          </div>

          {/* Items Table */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-medium">{t('documents.items')}</Label>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="inv-add-item" className="h-7 text-xs">
                <Plus size={12} className="mr-1" />
                {t('documents.addItem')}
              </Button>
            </div>
            <div className="border border-border rounded overflow-x-auto">
              <TooltipProvider delayDuration={200}>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-secondary/50 text-muted-foreground">
                      <th className="py-1.5 px-1.5 text-left font-medium w-10">#</th>
                      <th className="py-1.5 px-1.5 text-left font-medium min-w-[200px]">Description</th>
                      <th className="py-1.5 px-1.5 text-right font-medium w-[90px]">PRICE (USD)</th>
                      <th className="py-1.5 px-1.5 text-right font-medium w-16">QTY</th>
                      <th className="py-1.5 px-1.5 text-center font-medium w-14">UNIT</th>
                      <th className="py-1.5 px-1.5 text-right font-medium w-14">CTNS</th>
                      <th className="py-1.5 px-1.5 text-right font-medium w-16">CBM</th>
                      <th className="py-1.5 px-1.5 text-right font-medium w-[70px]">G.W.(kg)</th>
                      <th className="py-1.5 px-1.5 text-right font-medium w-[70px]">N.W.(kg)</th>
                      <th className="py-1.5 px-1.5 text-right font-medium w-24">TOTAL</th>
                      <th className="w-7"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => {
                      const lineTotal = isCtnMode && item.ctns > 0
                        ? item.price * item.qty * item.ctns
                        : item.price * item.qty;
                      const sug = priceSuggestions[i];
                      const matchStatus = priceMatches[i];
                      return (
                        <tr key={i} className="border-t border-border/50 hover:bg-muted/10" data-testid={`inv-item-${i}`}>
                          <td className="py-0.5 px-1.5">
                            <Input value={item.item_no}
                              onChange={e => updateItem(i, 'item_no', e.target.value)}
                              className="h-7 text-xs w-9 p-1 text-center"
                            />
                          </td>
                          <td className="py-0.5 px-1.5">
                            <Input value={item.description}
                              onChange={e => updateItem(i, 'description', e.target.value)}
                              className="h-7 text-xs" placeholder="Producto..."
                            />
                          </td>
                          <td className="py-0.5 px-1.5">
                            <div className="flex items-center gap-0.5">
                              <Input type="number" step="0.01"
                                value={item.price || ''}
                                onChange={e => updateItem(i, 'price', parseFloat(e.target.value) || 0)}
                                className={`h-7 text-xs text-right w-[70px] p-1 ${matchStatus === 'match' ? 'border-emerald-500/50' : matchStatus === 'mismatch' ? 'border-amber-500/50' : ''}`}
                              />
                              {sug && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => applySuggestion(i)}
                                      className={`p-0.5 rounded ${matchStatus === 'match' ? 'text-emerald-400' : 'text-amber-400 hover:bg-amber-500/10'}`}
                                    >
                                      {matchStatus === 'match' ? <Check size={12} /> : <AlertTriangle size={12} />}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    <p className="font-medium">{sug.product_name}</p>
                                    <p>Precio registrado: ${sug.unit_price} {sug.currency || 'USD'}/{sug.unit || 'pza'}</p>
                                    {matchStatus === 'mismatch' && <p className="text-amber-400">Click para aplicar</p>}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                          <td className="py-0.5 px-1.5">
                            <Input type="number" value={item.qty || ''}
                              onChange={e => updateItem(i, 'qty', parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs text-right w-14 p-1"
                            />
                          </td>
                          <td className="py-0.5 px-1.5">
                            <Input value={item.unit}
                              onChange={e => updateItem(i, 'unit', e.target.value)}
                              className="h-7 text-xs text-center w-12 p-1"
                            />
                          </td>
                          <td className="py-0.5 px-1.5">
                            <Input type="number" value={item.ctns || ''}
                              onChange={e => updateItem(i, 'ctns', parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs text-right w-12 p-1"
                            />
                          </td>
                          <td className="py-0.5 px-1.5">
                            <Input type="number" step="0.01" value={item.cbm || ''}
                              onChange={e => updateItem(i, 'cbm', parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs text-right w-14 p-1"
                            />
                          </td>
                          <td className="py-0.5 px-1.5">
                            <Input type="number" value={item.gw || ''}
                              onChange={e => updateItem(i, 'gw', parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs text-right w-[60px] p-1"
                            />
                          </td>
                          <td className="py-0.5 px-1.5">
                            <Input type="number" value={item.nw || ''}
                              onChange={e => updateItem(i, 'nw', parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs text-right w-[60px] p-1"
                            />
                          </td>
                          <td className="py-0.5 px-1.5 text-right font-mono text-xs font-medium">
                            ${lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-0.5 px-0.5">
                            {items.length > 1 && (
                              <button onClick={() => removeItem(i)} className="text-destructive hover:bg-destructive/10 p-0.5 rounded">
                                <Trash2 size={11} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-secondary/30">
                      <td colSpan={2} className="py-1.5 px-1.5 text-right font-medium text-xs">TOTALS</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-xs font-medium">{totalCtns || ''}</td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-xs font-medium">{totalCbm ? totalCbm.toFixed(2) : ''}</td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-xs font-medium">{totalGw ? totalGw.toLocaleString() : ''}</td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-xs font-medium">{totalNw ? totalNw.toLocaleString() : ''}</td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-xs font-semibold text-primary">
                        ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </TooltipProvider>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="h-8 text-xs">{t('common.cancel')}</Button>
          <Button onClick={handleGenerate} disabled={generating} data-testid="inv-generate-btn" className="h-8 text-xs">
            {generating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
            {t('documents.downloadXLSX')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
